import React from "react";
import { formatLovelace, unixToDate, formatRelativeTime } from "@/lib/format";
import { Badge, CopyBtn } from "@/components/primitives";

// ---------- Cardanoscan-style per-tx row for the addresses page ----------
export const AddressTransactionsList = ({ rows, amountMode, onPickTx, onPickBlock, loading }) => {
  if (loading && (!rows || rows.length === 0)) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-[68px] rounded-xl border border-primary/15 bg-card/30 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return <div className="text-[12px] text-muted-foreground/60 mono py-6">no transactions</div>;
  }
  return (
    <div data-testid="address-tx-list" className="space-y-2">
      {rows.map((r, i) => {
        const spent = BigInt(r.ada_spent || "0");
        const received = BigInt(r.ada_received || "0");
        const net = received - spent;
        const direction = net > 0n ? "in" : net < 0n ? "out" : "self";
        const dirBadge = direction === "in"
          ? { label: "IN", tone: "success" }
          : direction === "out"
          ? { label: "OUT", tone: "danger" }
          : { label: "SELF", tone: "info" };
        return (
          <div
            key={`${r.tx_hash}-${i}`}
            data-testid={`address-tx-${i}`}
            className="relative overflow-hidden rounded-xl border border-primary/20 bg-card/30 backdrop-blur-md hover:border-primary/40 hover:bg-primary/[0.03] transition-colors px-5 py-4 space-y-3"
          >
            {/* Hash + time */}
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 min-w-0">
                <CopyBtn value={r.tx_hash} label="tx" />
                <button
                  type="button"
                  onClick={() => onPickTx?.(r.tx_hash)}
                  title="Open transaction"
                  className="font-mono text-[12px] text-primary text-glow break-all hover:underline decoration-dotted underline-offset-2"
                >
                  {r.tx_hash}
                </button>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mono">
                {r.block_time ? unixToDate(r.block_time) : "—"}{" "}
                <span className="text-muted-foreground/60">· {formatRelativeTime(r.block_time)}</span>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-x-4 gap-y-3 items-end pt-3 border-t border-primary/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono mb-0.5">Block</div>
                <button
                  type="button"
                  onClick={() => onPickBlock?.(r.block_height)}
                  className="font-mono text-[13px] text-primary text-glow hover:underline decoration-dotted underline-offset-2"
                >
                  #{Number(r.block_height).toLocaleString("en-US")}
                </button>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono mb-0.5">Fees</div>
                <span className="font-mono text-[13px] tabnum text-muted-foreground">{formatLovelace(r.fee, amountMode)}</span>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono mb-0.5">ADA Spent</div>
                <span className="font-mono text-[13px] tabnum text-rose-400 whitespace-nowrap">
                  {spent > 0n ? `−${formatLovelace(spent.toString(), amountMode)}` : "—"}
                </span>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono mb-0.5">ADA Received</div>
                <span className="font-mono text-[13px] tabnum text-emerald-400 whitespace-nowrap">
                  {received > 0n ? `+${formatLovelace(received.toString(), amountMode)}` : "—"}
                </span>
              </div>
              <div className="justify-self-end self-end">
                <Badge tone={dirBadge.tone}>{dirBadge.label}</Badge>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
